"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Scale, Loader2, Calendar, Wallet, TrendingDown } from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/config";
import { toast } from "react-hot-toast";

export default function TeacherLeaveBalance() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherId, setTeacherId] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        const id = user._id || user.id;
        if (id) setTeacherId(id);
      } catch (e) {
        toast.error("Could not load profile");
      }
    }
  }, []);

  useEffect(() => {
    if (!teacherId) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = API_ENDPOINTS.leaveBalance(`year=${year}&teacherId=${teacherId}`);
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) setBalance(data.data || []);
        else setBalance([]);
      } catch (e) {
        toast.error("Failed to load leave balance");
        setBalance([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, teacherId]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const myBalance = balance[0] || null;
  const rows = myBalance?.byLeaveType || [];

  const summary = myBalance
    ? myBalance.byLeaveType.reduce(
        (acc, lt) => {
          const payType = lt.payType || "unpaid";
          acc[payType].used += lt.usedDays ?? 0;
          acc[payType].balance += lt.balance ?? 0;
          acc.totalUsed += lt.usedDays ?? 0;
          acc.totalBalance += lt.balance ?? 0;
          return acc;
        },
        { paid: { used: 0, balance: 0 }, unpaid: { used: 0, balance: 0 }, totalUsed: 0, totalBalance: 0 }
      )
    : null;

  if (!teacherId) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        Loading your profile…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
            <Scale className="h-5 w-5 text-[#0263cc]" />
            My Leave Balance
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            Your leave for the year: used days, balance remaining, and paid vs unpaid.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <Calendar className="h-3.5 w-3" /> Year
              </label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[100px] bg-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500">
              No leave balance data for this year. Leave types with &quot;Max days per year&quot; and approved leaves will show here.
            </div>
          ) : (
            <>
              {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50/80 border-b border-slate-200/80">
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <Wallet className="h-3.5 w-3" /> Paid leave used
                    </p>
                    <p className="text-lg font-semibold text-slate-800 mt-0.5">{summary.paid.used}</p>
                    <p className="text-xs text-emerald-600">Balance: {summary.paid.balance}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                      <TrendingDown className="h-3.5 w-3" /> Unpaid leave used
                    </p>
                    <p className="text-lg font-semibold text-slate-800 mt-0.5">{summary.unpaid.used}</p>
                    <p className="text-xs text-amber-600">Balance: {summary.unpaid.balance}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-emerald-200 p-3 sm:col-span-2">
                    <p className="text-xs font-medium text-slate-500">Total balance remaining</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-0.5">{summary.totalBalance}</p>
                    <p className="text-xs text-slate-500">Total used this year: {summary.totalUsed} days</p>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead className="font-semibold">Leave Type</TableHead>
                      <TableHead className="font-semibold">Paid / Unpaid</TableHead>
                      <TableHead className="font-semibold">Used</TableHead>
                      <TableHead className="font-semibold">Max</TableHead>
                      <TableHead className="font-semibold">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={`${row.leaveTypeId}-${idx}`} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium">
                          {row.name} ({row.code})
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              row.payType === "paid"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-amber-100 text-amber-800"
                            }
                          >
                            {row.payType === "paid" ? "Paid" : "Unpaid"}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.usedDays}</TableCell>
                        <TableCell>{row.maxDays ?? "—"}</TableCell>
                        <TableCell>
                          {row.balance != null ? (
                            <Badge
                              className={
                                row.balance >= 0
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-red-100 text-red-800"
                              }
                            >
                              {row.balance}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
